import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, Loader2, ShoppingCart, Shield, Zap, Award, Star, Clock, Lock, ChevronDown, Users } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { searchInstagram, getPrices } from '../services/api';
import { useCart } from '../contexts/CartContext';
import { useUI } from '../contexts/UIContext';
import CartModal from '../components/CartModal';
import Toast from '../components/Toast';
import PostSelector from '../components/PostSelector';

const InstagramIcon = ({ className = "w-9 h-9" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
  </svg>
);

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="w-9 h-9" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const FacebookIcon = ({ className = "w-9 h-9" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none">
    <rect width="24" height="24" rx="5" fill="#1877F2"/>
    <path d="M13.5 21v-7.5h2.25l.375-3H13.5V8.625c0-.75.375-1.125 1.125-1.125H16.5V5.25S15.375 5 14.25 5C11.625 5 10.5 6.375 10.5 8.625V10.5H8.25v3H10.5V21h3z" fill="white"/>
  </svg>
);

const YouTubeIcon = ({ className = "w-9 h-9" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none">
    <rect x="2" y="5" width="20" height="14" rx="3" fill="white"/>
    <path d="M10 8.5l7 3.5-7 3.5V8.5z" fill="#FF0000"/>
  </svg>
);

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart, getCartCount, cartItems } = useCart();
  const { setHideWhatsApp } = useUI();
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('instagram');
  const [searchResult, setSearchResult] = useState(null);
  const [showQuantitySelector, setShowQuantitySelector] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [prices, setPrices] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [showPrivateAccountModal, setShowPrivateAccountModal] = useState(false);
  const [showPostSelector, setShowPostSelector] = useState(false);
  const [pendingCartAction, setPendingCartAction] = useState(null);
  const [isProfilePrivate, setIsProfilePrivate] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [searchTimedOut, setSearchTimedOut] = useState(false);
  const [searchFailCount, setSearchFailCount] = useState(0);
  const searchResultRef = useRef(null);
  const searchBoxRef = useRef(null);
  const serviceRef = useRef(null);
  const packageRef = useRef(null);
  const actionsRef = useRef(null);

  useEffect(() => { loadPrices(); }, [selectedPlatform, selectedService]);

  useEffect(() => {
    if (searchFailCount >= 2 && searchTimedOut) {
      const t = setTimeout(() => handleSkipVerification(), 800);
      return () => clearTimeout(t);
    }
  }, [searchFailCount, searchTimedOut]);

  useEffect(() => {
    if (location.state?.openCart) {
      setIsCartOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const scrollToRef = (ref, offset = 80) => {
    if (!ref.current) return;
    const top = ref.current.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const scrollToRefBottom = (ref) => {
    if (!ref.current) return;
    const bottom = ref.current.getBoundingClientRect().bottom + window.pageYOffset;
    window.scrollTo({ top: bottom - window.innerHeight, behavior: 'smooth' });
  };

  useEffect(() => {
    if (searchResult && searchBoxRef.current) {
      setTimeout(() => scrollToRef(searchBoxRef, 80), 150);
    }
  }, [searchResult]);

  useEffect(() => {
    if (showQuantitySelector) {
      const isMobile = window.innerWidth < 640;
      if (isMobile && searchResultRef.current) {
        setTimeout(() => scrollToRef(searchResultRef, 70), 150);
      } else if (!isMobile && searchBoxRef.current) {
        setTimeout(() => scrollToRef(searchBoxRef, 80), 150);
      }
    }
  }, [showQuantitySelector]);

  useEffect(() => {
    if (selectedService) {
      const isMobile = window.innerWidth < 640;
      if (isMobile && actionsRef.current) {
        setTimeout(() => scrollToRefBottom(actionsRef), 150);
      } else if (!isMobile && searchResultRef.current) {
        setTimeout(() => scrollToRef(searchResultRef, 80), 150);
      }
    }
  }, [selectedService]);

  useEffect(() => {
    if (selectedPackage && actionsRef.current) {
      setTimeout(() => scrollToRefBottom(actionsRef), 150);
    }
    setHideWhatsApp(!!selectedPackage);
  }, [selectedPackage]);

  useEffect(() => {
    setUsername('');
    setSearchResult(null);
    setShowQuantitySelector(false);
    setSelectedService(null);
    setSelectedPackage(null);
  }, [selectedPlatform]);

  const mapServiceToBackend = (service) => {
    const mapping = { 'audience': 'followers', 'engagement': 'likes', 'reach': 'views' };
    return mapping[service] || 'followers';
  };

  const loadPrices = async () => {
    try {
      const data = await getPrices(selectedPlatform);
      if (selectedService) {
        const backendType = mapServiceToBackend(selectedService);
        setPrices(data.data.filter(price => (price.serviceType || 'followers') === backendType));
      } else {
        setPrices(data.data);
      }
    } catch (error) {
      console.error('Error loading prices:', error);
    }
  };

  const handleSearch = async () => {
    if (!username.trim()) return;
    if (document.activeElement) document.activeElement.blur();
    setLoading(true);
    setSearchTimedOut(false);
    setSearchResult(null);

    const timeoutId = setTimeout(() => {
      setLoading(false);
      setSearchTimedOut(true);
      setSearchFailCount(prev => prev + 1);
    }, 30000);

    try {
      if (selectedPlatform === 'facebook') {
        const getFbDisplayName = (url) => {
          try {
            const clean = url.trim().replace(/\/$/, '');
            const parts = clean.split('/').filter(Boolean);
            return parts[parts.length - 1] || url;
          } catch { return url; }
        };

        let pageUrl = username.trim();
        let pageName;

        if (username.includes('facebook.com/')) {
          pageName = getFbDisplayName(username);
        } else {
          const u = username.replace('@', '').trim();
          pageUrl = `https://www.facebook.com/${u}`;
          pageName = u;
        }

        const profile = {
          username: pageUrl,
          displayName: pageName,
          followers: '-',
          profilePic: `https://ui-avatars.com/api/?name=${pageName}&size=200&background=1877F2&color=fff`,
          pageUrl
        };

        setSearchResult(profile);
        setProfileData({ username: pageName, pageUrl });
        setIsProfilePrivate(false);
        setShowQuantitySelector(true);
      } else {
        const data = await searchInstagram(username, selectedPlatform);
        const fallbackColor = selectedPlatform === 'tiktok' ? '000000' : selectedPlatform === 'youtube' ? 'FF0000' : 'E1306C';
        const finalProfilePic = data.data.profilePic || `https://ui-avatars.com/api/?name=${username}&size=200&background=${fallbackColor}&color=fff`;

        const profile = {
          username: data.data.username,
          followers: typeof data.data.followers === 'number' ? data.data.followers.toLocaleString('en-US') : data.data.followers,
          profilePic: finalProfilePic,
          ...(selectedPlatform === 'youtube' && data.data.website && { channelUrl: data.data.website })
        };

        setSearchResult(profile);
        setProfileData(data.data);
        setIsProfilePrivate(data.data.isPrivate || false);
        setShowQuantitySelector(false);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchFailCount(prev => prev + 1);
      setSearchTimedOut(true);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const handleSkipVerification = () => {
    const raw = username.trim().replace('@', '').replace(/\s+/g, '');
    const fallbackColor = selectedPlatform === 'tiktok' ? '000000' : selectedPlatform === 'youtube' ? 'FF0000' : 'E1306C';
    const profile = {
      username: raw,
      followers: '-',
      profilePic: `https://ui-avatars.com/api/?name=${raw}&size=200&background=${fallbackColor}&color=fff`,
    };
    setSearchResult(profile);
    setProfileData({ username: raw });
    setIsProfilePrivate(false);
    setSearchTimedOut(false);
    setSearchFailCount(0);
    setShowQuantitySelector(false);
  };

  const handleGrowNow = () => {
    if (isProfilePrivate) {
      setShowPrivateAccountModal(true);
      return;
    }
    setShowQuantitySelector(true);
    setSelectedService(null);
    setSelectedPackage(null);
  };

  const needsPostSelection = () => {
    return (selectedPlatform === 'instagram' || selectedPlatform === 'tiktok' || selectedPlatform === 'youtube') &&
           (selectedService === 'engagement' || selectedService === 'reach');
  };

  const handleAddToCart = () => {
    if (!selectedPackage) { alert('Please select a package'); return; }
    if (needsPostSelection()) {
      setPendingCartAction('cart');
      setShowPostSelector(true);
    } else {
      proceedAddToCart();
    }
  };

  const handleCheckout = () => {
    if (!selectedPackage) { alert('Please select a package'); return; }
    if (needsPostSelection()) {
      setPendingCartAction('checkout');
      setShowPostSelector(true);
    } else {
      proceedCheckout();
    }
  };

  const proceedAddToCart = (selectedPosts = null) => {
    const cartItem = {
      username: searchResult.username,
      displayName: searchResult.displayName || searchResult.username,
      quantity: selectedPackage.quantity,
      price: selectedPackage.price,
      platform: selectedPlatform,
      serviceType: mapServiceToBackend(selectedService),
      profilePic: searchResult.profilePic,
      ...(selectedPosts && { selectedPosts }),
      ...(selectedPlatform === 'facebook' && searchResult.pageUrl && { pageUrl: searchResult.pageUrl })
    };

    addToCart(cartItem);
    setToastMessage('Added to cart');
    setToastType('success');
    setShowToast(true);

    setSelectedService(null);
    setSelectedPackage(null);
    const isMobile = window.innerWidth < 640;
    if (isMobile && searchResultRef.current) {
      setTimeout(() => scrollToRef(searchResultRef, 70), 150);
    } else if (!isMobile && searchBoxRef.current) {
      setTimeout(() => scrollToRef(searchBoxRef, 80), 150);
    }
  };

  const proceedCheckout = (selectedPosts = null) => {
    const cartItem = {
      username: searchResult.username,
      displayName: searchResult.displayName || searchResult.username,
      quantity: selectedPackage.quantity,
      price: selectedPackage.price,
      platform: selectedPlatform,
      serviceType: mapServiceToBackend(selectedService),
      profilePic: searchResult.profilePic,
      ...(selectedPosts && { selectedPosts }),
      ...(selectedPlatform === 'facebook' && searchResult.pageUrl && { pageUrl: searchResult.pageUrl })
    };

    addToCart(cartItem);
    const allItems = [...cartItems, cartItem];
    navigate('/checkout', { state: { cartItems: allItems } });
  };

  const handlePostsSelected = (selectedPosts, action) => {
    setShowPostSelector(false);
    if (action === 'cart') {
      proceedAddToCart(selectedPosts);
    } else if (action === 'checkout') {
      proceedCheckout(selectedPosts);
    }
    setPendingCartAction(null);
  };

  const platforms = [
    { name: 'Instagram', id: 'instagram', icon: InstagramIcon, gradient: 'linear-gradient(135deg, #833AB4 0%, #E1306C 50%, #FCAF45 100%)', placeholder: '@Username or Profile Link' },
    { name: 'TikTok', id: 'tiktok', icon: TikTokIcon, gradient: 'linear-gradient(135deg, #000000 0%, #00f2ea 50%, #ff0050 100%)', placeholder: '@Username or Profile Link' },
    { name: 'YouTube', id: 'youtube', icon: YouTubeIcon, gradient: 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)', placeholder: '@Username or Channel Link' },
    { name: 'Facebook', id: 'facebook', icon: FacebookIcon, gradient: 'linear-gradient(135deg, #1877F2 0%, #0A5FD9 100%)', placeholder: 'Profile or Page Link' }
  ];

  const currentPlatform = platforms.find(p => p.id === selectedPlatform);
  const CurrentIcon = currentPlatform.icon;

  return (
    <div className="min-h-screen bg-white">
      <Navbar
        selectedPlatform={selectedPlatform}
        onPlatformSelect={(p) => { setSelectedPlatform(p); setSearchFailCount(0); setSearchTimedOut(false); setSearchResult(null); }}
        onCartOpen={() => setIsCartOpen(true)}
      />

      {/* Platform Selection */}
      <section className="py-8 sm:py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">

          <div className="text-center mb-8 max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 px-4 py-1.5 rounded-full mb-4">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-semibold text-green-700">All Services Active</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-3">
              Boost Your Online Presence
            </h1>
            <div className="flex justify-center gap-4 sm:gap-6 flex-wrap">
              <div className="flex items-center gap-1.5">
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                <span className="text-xs font-bold text-gray-700">4.9/5 Rating</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Shield className="w-4 h-4 text-green-500" />
                <span className="text-xs font-bold text-gray-700">Secure</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4 text-blue-500" />
                <span className="text-xs font-bold text-gray-700">Guaranteed</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-4 sm:flex sm:justify-center gap-2 sm:gap-4 mb-8 sm:mb-10">
            {platforms.map((platform) => {
              const Icon = platform.icon;
              const isSelected = selectedPlatform === platform.id;
              return (
                <button
                  key={platform.id}
                  onClick={() => setSelectedPlatform(platform.id)}
                  className={`relative flex flex-col items-center gap-2 p-3 sm:p-6 rounded-2xl transition-all duration-300 sm:min-w-[120px] ${
                    isSelected
                      ? 'bg-white border-2 border-pink-500 shadow-2xl scale-105 sm:scale-110'
                      : 'bg-white border-2 border-gray-200 shadow-md hover:border-pink-300 opacity-60 hover:opacity-100'
                  }`}
                >
                  {isSelected && (
                    <span className="absolute -top-2 -right-2 w-5 h-5 bg-pink-500 rounded-full flex items-center justify-center shadow-md">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                  )}
                  <div
                    className={`w-12 h-12 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center transition-all duration-300 ${isSelected ? 'shadow-lg' : ''}`}
                    style={{ background: platform.gradient }}
                  >
                    <Icon className="text-white w-7 h-7 sm:w-11 sm:h-11" />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Search Box */}
          <div ref={searchBoxRef} className="max-w-4xl mx-auto px-2 sm:px-0">
            <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-xl overflow-hidden hover:border-pink-300 transition-colors">
              <div className="flex flex-col sm:flex-row items-center">
                <div className="flex items-center flex-1 w-full px-5 sm:px-8 gap-3 sm:gap-4 py-4 sm:py-0">
                  <div className="w-12 sm:w-14 h-12 sm:h-14 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0" style={{ background: currentPlatform.gradient }}>
                    <CurrentIcon className="text-white w-6 sm:w-7 h-6 sm:h-7" />
                  </div>
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => { setUsername(e.target.value); setSearchFailCount(0); setSearchTimedOut(false); }}
                      placeholder=""
                      className="w-full py-4 sm:py-7 bg-transparent text-gray-800 text-base sm:text-lg outline-none"
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    {!username && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 text-base sm:text-lg pointer-events-none placeholder-pulse">
                        {currentPlatform.placeholder}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="bg-gradient-to-r from-pink-600 to-purple-600 text-white w-full sm:w-auto px-8 sm:px-14 py-4 sm:py-7 text-base sm:text-lg font-bold hover:from-pink-700 hover:to-purple-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2 sm:gap-3 shadow-lg rounded-b-2xl sm:rounded-b-none"
                >
                  {loading ? (
                    <><Loader2 className="w-5 sm:w-6 h-5 sm:h-6 animate-spin" /><span>Searching...</span></>
                  ) : (
                    <span>Search</span>
                  )}
                </button>
              </div>
            </div>
            {loading && (
              <p className="text-center text-sm text-gray-500 font-medium mt-3 animate-pulse">
                This may take up to 30 seconds...
              </p>
            )}
            {searchTimedOut && (
              <div className="text-center mt-3">
                {searchFailCount < 2 ? (
                  <>
                    <p className="text-sm text-gray-500 mb-2">Could not load profile. Please try again.</p>
                    <button
                      onClick={handleSearch}
                      className="bg-gradient-to-r from-pink-600 to-purple-600 text-white px-10 py-3 rounded-xl font-bold text-base hover:from-pink-700 hover:to-purple-700 transition"
                    >
                      Try again
                    </button>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 animate-pulse">Loading options...</p>
                )}
              </div>
            )}
            <div className="flex justify-center items-center gap-4 sm:gap-6 mt-4 flex-wrap">
              <span className="flex items-center gap-1.5 text-xs text-gray-500">
                <Shield className="w-3.5 h-3.5 text-green-500" />
                <span>Safe &amp; Confidential</span>
              </span>
              <span className="text-gray-300 hidden sm:block">|</span>
              <span className="flex items-center gap-1.5 text-xs text-gray-500">
                <Zap className="w-3.5 h-3.5 text-yellow-500" />
                <span>Fast Delivery</span>
              </span>
              <span className="text-gray-300 hidden sm:block">|</span>
              <span className="flex items-center gap-1.5 text-xs text-gray-500">
                <CheckCircle className="w-3.5 h-3.5 text-blue-500" />
                <span>Satisfaction Guaranteed</span>
              </span>
            </div>
          </div>

          {/* Search Result */}
          {searchResult && (
            <div ref={searchResultRef} className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-8 max-w-2xl mx-auto mt-8 sm:mt-12 shadow-xl mx-2 sm:mx-auto">
              {selectedPlatform !== 'facebook' && (
                <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 mb-5 pb-5 border-b border-gray-100">
                  <img
                    src={searchResult.profilePic}
                    alt={searchResult.username}
                    className="w-14 sm:w-16 h-14 sm:h-16 rounded-full border-3 border-pink-200 object-cover shadow-md flex-shrink-0"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      e.target.src = `https://ui-avatars.com/api/?name=${searchResult.username}&size=200&background=E1306C&color=fff`;
                    }}
                  />
                  <div className="flex-1 text-center sm:text-left">
                    <p className="text-gray-900 font-bold text-lg sm:text-xl">@{searchResult.username}</p>
                    <p className="text-gray-600 text-xs sm:text-sm flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      {searchResult.followers}
                    </p>
                  </div>
                  <div className="bg-green-100 px-3 py-1 rounded-full flex-shrink-0">
                    <CheckCircle className="w-4 sm:w-5 h-4 sm:h-5 text-green-600" />
                  </div>
                </div>
              )}

              {!showQuantitySelector ? (
                <div className="space-y-3">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 text-center border border-green-200">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <p className="text-green-700 text-sm font-bold">Profile verified!</p>
                    </div>
                    <p className="text-gray-600 text-xs">Click Continue to choose your growth package</p>
                  </div>
                  <button
                    onClick={handleGrowNow}
                    className="w-full bg-gradient-to-r from-pink-600 to-purple-600 text-white py-4 rounded-xl text-lg font-bold hover:from-pink-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                  >
                    Continue
                  </button>
                </div>
              ) : !selectedService ? (
                <div ref={serviceRef} className="space-y-4 sm:space-y-6">
                  <div className="text-center">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">Choose Your Growth Package</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Select the strategy you want to implement</p>
                  </div>

                  {(() => {
                    const allServices = [
                      {
                        id: 'audience',
                        name: selectedPlatform === 'youtube' ? 'Subscribers' : selectedPlatform === 'facebook' ? 'Page Followers' : 'Followers',
                        icon: '👥',
                        color: 'from-pink-600 to-purple-600',
                        type: 'followers'
                      },
                      { id: 'engagement', name: 'Likes', icon: '❤️', color: 'from-red-600 to-pink-600', type: 'likes' },
                      { id: 'reach', name: 'Views', icon: '👁️', color: 'from-blue-600 to-cyan-600', type: 'views' }
                    ];

                    const filteredServices = allServices.filter(service => {
                      if (selectedPlatform === 'facebook') {
                        return service.id === 'audience' || service.id === 'engagement';
                      }
                      return true;
                    });

                    const gridClass = filteredServices.length === 2
                      ? 'grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 max-w-lg mx-auto'
                      : 'grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4';

                    return (
                      <div className={gridClass}>
                        {filteredServices.map((service) => (
                          <button
                            key={service.id}
                            onClick={() => setSelectedService(service.id)}
                            className={`p-4 sm:p-6 rounded-xl border-2 transition-all hover:shadow-lg flex sm:flex-col items-center gap-4 sm:gap-0 text-left sm:text-center w-full ${
                              selectedService === service.id
                                ? 'border-pink-600 bg-pink-50 shadow-xl'
                                : 'border-gray-200 bg-white hover:border-pink-300'
                            }`}
                          >
                            <div className="text-3xl sm:text-4xl sm:mb-3 flex-shrink-0">{service.icon}</div>
                            <div className="flex-1">
                              <h4 className="text-base sm:text-lg font-bold text-gray-900 sm:mb-2">{service.name}</h4>
                              <p className="text-xs text-gray-500 sm:text-gray-600 mt-0.5">
                                {service.id === 'audience' ? 'Gain new followers' : service.id === 'engagement' ? 'More likes on posts' : 'Increase your views'}
                              </p>
                            </div>
                            <svg className="sm:hidden w-5 h-5 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div ref={packageRef} className="space-y-4 sm:space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="text-center flex-1">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">
                        Choose Your {selectedService === 'audience' ? (selectedPlatform === 'youtube' ? 'Subscribers' : 'Followers') : selectedService === 'engagement' ? 'Likes' : 'Views'} Package
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600">Select the package that suits you</p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedService(null);
                        setSelectedPackage(null);
                        const isMobile = window.innerWidth < 640;
                        if (isMobile && searchResultRef.current) {
                          setTimeout(() => scrollToRef(searchResultRef, 70), 150);
                        } else if (!isMobile && searchBoxRef.current) {
                          setTimeout(() => scrollToRef(searchBoxRef, 80), 150);
                        }
                      }}
                      className="text-gray-600 hover:text-gray-900 transition text-sm font-semibold ml-4"
                    >
                      ← Back
                    </button>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                    {prices.slice(0, 8).map((priceItem, index) => {
                      const isPopular = index === 4 || index === 5;
                      return (
                        <button
                          key={priceItem.id}
                          onClick={() => setSelectedPackage(priceItem)}
                          className={`relative p-3 sm:p-4 rounded-xl border-2 transition-all ${
                            selectedPackage?.id === priceItem.id
                              ? 'border-pink-600 bg-pink-50 shadow-xl sm:scale-105'
                              : 'border-gray-200 bg-white hover:border-pink-300 hover:shadow-lg'
                          }`}
                        >
                          {isPopular && (
                            <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 z-10">
                              <span className="bg-gradient-to-r from-pink-600 to-purple-600 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap">
                                POPULAR
                              </span>
                            </div>
                          )}
                          <div className="text-center">
                            <div className="text-xl sm:text-2xl font-extrabold text-gray-900 mb-0.5 sm:mb-1">
                              {priceItem.quantity.toLocaleString('en-US')}
                            </div>
                            <div className="text-xs text-gray-500 mb-1 sm:mb-2 uppercase tracking-wide">
                              {selectedService === 'audience' ? (selectedPlatform === 'youtube' ? 'Subscribers' : 'Followers') : selectedService === 'engagement' ? 'Likes' : 'Views'}
                            </div>
                            <div className="text-base sm:text-lg font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
                              €{parseFloat(priceItem.price).toFixed(2)}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div ref={actionsRef} className="space-y-3">
                    {selectedPackage && (
                      <div className="bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-200">
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div>
                            <span className="text-xs text-gray-600">Selected Package:</span>
                            <p className="font-bold text-sm text-gray-900 capitalize">
                              {selectedService === 'audience' ? (selectedPlatform === 'youtube' ? '👥 Subscribers' : '👥 Followers') : selectedService === 'engagement' ? '❤️ Likes' : '👁️ Views'}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="text-xs text-gray-600">Quantity:</span>
                            <p className="font-bold text-sm text-gray-900">
                              {selectedPackage.quantity.toLocaleString('en-US')}
                            </p>
                          </div>
                        </div>
                        <div className="flex justify-between items-center pt-3 border-t border-gray-300">
                          <span className="text-xs sm:text-sm text-gray-600 font-semibold">Total:</span>
                          <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
                            €{parseFloat(selectedPackage.price).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="space-y-3">
                      {(selectedService === 'engagement' || selectedService === 'reach') ? (
                        <button
                          onClick={handleCheckout}
                          disabled={!selectedPackage}
                          className="w-full bg-gradient-to-r from-pink-600 to-purple-600 text-white py-4 rounded-xl text-base sm:text-lg font-bold hover:from-pink-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl sm:transform sm:hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {selectedPackage ? '⚡ Continue' : 'Select a package'}
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={handleAddToCart}
                            disabled={!selectedPackage}
                            className="w-full bg-white border-2 border-pink-600 text-pink-600 py-4 rounded-xl text-base sm:text-lg font-bold hover:bg-pink-50 transition-all shadow-lg hover:shadow-xl sm:transform sm:hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            🛍️ {selectedPackage ? 'Keep Shopping' : 'Select a package'}
                          </button>
                          <button
                            onClick={handleCheckout}
                            disabled={!selectedPackage}
                            className="w-full bg-gradient-to-r from-pink-600 to-purple-600 text-white py-4 rounded-xl text-base sm:text-lg font-bold hover:from-pink-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl sm:transform sm:hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {selectedPackage ? '✅ Checkout' : 'Select a package'}
                          </button>
                        </>
                      )}
                    </div>

                    <p className="text-center text-xs text-gray-500 mt-3">
                      🔒 100% secure payment • ⭐ Satisfaction guaranteed
                    </p>
                    <div className="flex justify-center items-center gap-2 mt-2 flex-wrap">
                      <img src="/visa.svg" alt="Visa" className="h-7 w-auto bg-white px-1.5 py-0.5 rounded border border-gray-200" />
                      <img src="/mastercard.svg" alt="Mastercard" className="h-7 w-auto bg-white px-1.5 py-0.5 rounded border border-gray-200" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-12 sm:py-20 bg-gradient-to-br from-purple-600 via-pink-600 to-purple-700 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-white/20 border border-white/30 px-4 py-1.5 rounded-full mb-4">
              <CheckCircle className="w-4 h-4 text-green-300" />
              <span className="text-xs font-semibold text-white">Verified and up-to-date data</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">Results That Prove Our Excellence</h2>
            <p className="text-purple-100 text-sm sm:text-base max-w-2xl mx-auto">
              Thousands of brands trust GrowProPlus to transform their digital presence
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-4xl sm:text-5xl font-extrabold mb-2">10K+</div>
              <div className="text-purple-200 text-xs sm:text-sm font-semibold">Customers Served</div>
            </div>
            <div className="text-center">
              <div className="text-4xl sm:text-5xl font-extrabold mb-2">500K+</div>
              <div className="text-purple-200 text-xs sm:text-sm font-semibold">Orders Completed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl sm:text-5xl font-extrabold mb-2">99.8%</div>
              <div className="text-purple-200 text-xs sm:text-sm font-semibold">Success Rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl sm:text-5xl font-extrabold mb-2">24/7</div>
              <div className="text-purple-200 text-xs sm:text-sm font-semibold">Support</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">How Does It Work?</h2>
            <p className="text-gray-600 text-sm sm:text-base max-w-2xl mx-auto">
              Simple and fast — just 3 steps
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="relative">
              <div className="text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                  <span className="text-3xl sm:text-4xl font-extrabold text-white">1</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Select a Platform</h3>
                <p className="text-gray-600 text-sm">
                  Choose the platform you want to grow on
                </p>
              </div>
              <div className="hidden md:block absolute top-10 left-1/2 w-full h-0.5 bg-gradient-to-r from-pink-300 to-purple-300 -z-10"></div>
            </div>

            <div className="relative">
              <div className="text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                  <span className="text-3xl sm:text-4xl font-extrabold text-white">2</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Choose Your Strategy</h3>
                <p className="text-gray-600 text-sm">
                  Select followers, likes, or views — and the quantity
                </p>
              </div>
              <div className="hidden md:block absolute top-10 left-1/2 w-full h-0.5 bg-gradient-to-r from-pink-300 to-purple-300 -z-10"></div>
            </div>

            <div className="relative">
              <div className="text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                  <span className="text-3xl sm:text-4xl font-extrabold text-white">3</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Watch Your Growth</h3>
                <p className="text-gray-600 text-sm">
                  Complete your order and watch the results roll in
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">What Our Customers Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                ))}
              </div>
              <p className="text-gray-700 mb-4 text-sm italic">
                "Easy process, very professional service and visible growth in no time. Highly recommend."
              </p>
              <div className="flex items-center gap-3">
                <img src="https://randomuser.me/api/portraits/women/44.jpg" alt="Sarah Miller" className="w-10 h-10 rounded-full object-cover" />
                <div>
                  <p className="font-bold text-gray-900 text-sm">Sarah Miller</p>
                  <p className="text-xs text-gray-500">Digital Influencer</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                ))}
              </div>
              <p className="text-gray-700 mb-4 text-sm italic">
                "The support is excellent and everything is explained clearly. My digital presence finally has direction."
              </p>
              <div className="flex items-center gap-3">
                <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="James Wilson" className="w-10 h-10 rounded-full object-cover" />
                <div>
                  <p className="font-bold text-gray-900 text-sm">James Wilson</p>
                  <p className="text-xs text-gray-500">Entrepreneur</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                ))}
              </div>
              <p className="text-gray-700 mb-4 text-sm italic">
                "I didn't expect results this fast. The strategy is well thought out and focused on real growth. Excellent!"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-600 to-purple-700 flex items-center justify-center shadow-sm flex-shrink-0">
                  <span className="text-white font-black text-[9px] tracking-tight leading-none text-center">LUMI<br/>MEDIA</span>
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">Emma Davis</p>
                  <p className="text-xs text-gray-500">Content Creator</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-12 sm:py-20 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-2xl p-8 sm:p-12 border-4 border-green-200 relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none select-none z-0">
              <span className="absolute top-[1%] left-[2%] text-4xl sm:text-6xl opacity-60 rotate-[-15deg]">❤️</span>
              <span className="absolute top-[4%] left-[16%] text-3xl sm:text-5xl opacity-55 rotate-[8deg]">👍</span>
              <span className="absolute top-[1%] left-[30%] text-3xl sm:text-5xl opacity-50 rotate-[-10deg]">🚀</span>
              <span className="absolute top-[5%] left-[46%] text-3xl sm:text-5xl opacity-55 rotate-[12deg]">📸</span>
              <span className="absolute top-[2%] left-[62%] text-3xl sm:text-5xl opacity-50 rotate-[-8deg]">👁️</span>
              <span className="absolute top-[6%] left-[78%] text-3xl sm:text-5xl opacity-55 rotate-[15deg]">📈</span>
              <span className="absolute top-[1%] left-[90%] text-4xl sm:text-6xl opacity-60 rotate-[10deg]">➕</span>
              <span className="absolute top-[30%] left-[2%] text-3xl sm:text-5xl opacity-55 rotate-[12deg]">👁️</span>
              <span className="absolute top-[33%] left-[13%] text-4xl sm:text-6xl opacity-50 rotate-[-8deg]">📈</span>
              <span className="absolute top-[40%] left-[1%] text-4xl sm:text-6xl opacity-60 rotate-[8deg]">👍</span>
              <span className="absolute top-[43%] left-[17%] text-3xl sm:text-5xl opacity-55 rotate-[-10deg]">🚀</span>
              <span className="absolute top-[60%] left-[31%] text-3xl sm:text-5xl opacity-50 rotate-[8deg]">👁️</span>
              <span className="absolute top-[72%] left-[1%] text-3xl sm:text-5xl opacity-55 rotate-[-10deg]">➕</span>
              <span className="absolute top-[75%] left-[15%] text-4xl sm:text-6xl opacity-60 rotate-[8deg]">❤️</span>
              <span className="absolute top-[82%] left-[3%] text-4xl sm:text-6xl opacity-60 rotate-[10deg]">👁️</span>
              <span className="absolute top-[91%] left-[2%] text-4xl sm:text-6xl opacity-60 rotate-[-8deg]">📸</span>
              <span className="absolute top-[91%] left-[63%] text-3xl sm:text-5xl opacity-50 rotate-[-6deg]">❤️</span>
              <span className="absolute top-[91%] left-[79%] text-3xl sm:text-5xl opacity-55 rotate-[14deg]">👍</span>
              <span className="absolute top-[91%] left-[91%] text-4xl sm:text-6xl opacity-60 rotate-[-10deg]">📈</span>
            </div>
            <div className="text-center relative z-10">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-12 h-12 text-green-600" />
              </div>
              <div className="inline-block bg-white/80 backdrop-blur-sm rounded-2xl px-6 py-4 mb-6 shadow-sm">
                <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-2">
                  Commitment to Results
                </h2>
                <p className="text-base sm:text-lg text-gray-600">
                  We turn digital presence into real results.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                <div className="bg-green-50 p-6 rounded-2xl border-2 border-green-200">
                  <Clock className="w-10 h-10 text-green-600 mx-auto mb-3" />
                  <h3 className="font-bold text-gray-900 mb-2">Ongoing Support</h3>
                  <p className="text-sm text-gray-600">
                    Constant professional support whenever you need it
                  </p>
                </div>
                <div className="bg-green-50 p-6 rounded-2xl border-2 border-green-200">
                  <Award className="w-10 h-10 text-green-600 mx-auto mb-3" />
                  <h3 className="font-bold text-gray-900 mb-2">Excellence Guarantee</h3>
                  <p className="text-sm text-gray-600">
                    Committed to excellence. The right standard to grow.
                  </p>
                </div>
              </div>
              <div className="bg-gradient-to-r from-green-100 to-emerald-100 p-6 rounded-2xl border-2 border-green-300">
                <p className="text-gray-800 font-bold text-lg mb-2">
                  🔒 Professional strategy • Optimised processes • Focus on real results
                </p>
                <p className="text-sm text-gray-600">
                  The strategy chosen by thousands of growing content creators worldwide.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-gray-600 text-sm sm:text-base">
                Everything you need to know before getting started
              </p>
            </div>
            <div className="space-y-3">
              {[
                {
                  q: 'What payment methods are accepted?',
                  a: 'We accept all major credit and debit cards (Visa, Mastercard, Amex) via Stripe — secure and instant.'
                },
                {
                  q: 'Do I need to create an account?',
                  a: 'No, no account needed. Just follow the steps and you will receive everything by email.'
                },
                {
                  q: 'Is my data safe?',
                  a: 'Yes. We never ask for your password or account access. Your card data is handled exclusively by Stripe.'
                },
                {
                  q: 'What are the support hours?',
                  a: 'We are available every day of the week via email support.'
                },
                {
                  q: 'Are there any hidden fees?',
                  a: 'No. All prices and services are clearly shown on the site — no surprises.'
                },
                {
                  q: 'Do you offer support throughout the process?',
                  a: 'Yes. We monitor each step closely and offer dedicated support as needed.'
                }
              ].map((faq, index) => (
                <div key={index} className="border border-gray-200 rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition"
                  >
                    <span className="font-bold text-gray-900 text-sm sm:text-base pr-4">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${openFaq === index ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-5 border-t border-gray-100">
                      <p className="text-gray-600 text-sm leading-relaxed pt-4">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-5xl font-extrabold mb-4 leading-tight">Ready to grow?</h2>
          <p className="text-purple-100 text-base sm:text-lg mb-10 max-w-xl mx-auto">
            Join thousands of creators and brands who already grew with GrowProPlus.
          </p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="bg-white text-purple-700 font-extrabold px-10 py-4 sm:px-14 sm:py-5 rounded-2xl text-base sm:text-lg hover:bg-purple-50 transition shadow-xl hover:shadow-2xl transform hover:scale-105"
          >
            Get Started →
          </button>
          <div className="flex justify-center gap-8 mt-10 flex-wrap text-sm text-purple-200">
            <span className="flex items-center gap-2"><Shield className="w-4 h-4" /> Secure</span>
            <span className="flex items-center gap-2"><Zap className="w-4 h-4" /> Fast</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Guaranteed</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="container mx-auto px-4 py-8 sm:py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-8">
            <div>
              <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                <h3 className="text-xl font-extrabold text-white mb-3 cursor-pointer">GrowProPlus</h3>
              </Link>
              <p className="text-gray-400 text-xs sm:text-sm">
                Agency specialised in social media growth.
              </p>
            </div>

            <div>
              <h4 className="font-bold mb-3 sm:mb-4 text-sm">Navigation</h4>
              <ul className="space-y-2 text-gray-400 text-xs sm:text-sm">
                <li><button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="hover:text-white transition">Home</button></li>
                <li><button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="hover:text-white transition">Packages</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-3 sm:mb-4 text-sm">Legal</h4>
              <ul className="space-y-2 text-gray-400 text-xs sm:text-sm">
                <li><Link to="/terms" className="hover:text-white transition">Terms &amp; Conditions</Link></li>
                <li><Link to="/refund" className="hover:text-white transition">Refund Policy</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-3 sm:mb-4 text-sm">Support</h4>
              <ul className="space-y-2 text-gray-400 text-xs sm:text-sm">
                <li><Link to="/contact" className="hover:text-white transition">Contact Us</Link></li>
                <li><a href="mailto:growproplus@growproplus.com" className="hover:text-white transition">growproplus@growproplus.com</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 py-6 sm:py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
              <span className="bg-green-900 text-green-200 px-2 py-1 rounded text-xs">🔒 Secure</span>
              <span className="bg-blue-900 text-blue-200 px-2 py-1 rounded text-xs">✅ Verified</span>
              <span className="bg-purple-900 text-purple-200 px-2 py-1 rounded text-xs">⚡ Fast</span>
            </div>
            <div className="flex items-center gap-3 justify-center flex-wrap">
              <img src="/visa.svg" alt="Visa" className="h-8 w-auto bg-white px-2 py-1 rounded" />
              <img src="/mastercard.svg" alt="Mastercard" className="h-8 w-auto bg-white px-2 py-1 rounded" />
            </div>
          </div>

          <div className="border-t border-gray-700 pt-6 text-center">
            <p className="text-gray-400 text-xs sm:text-sm mb-2">© 2026 GrowProPlus. All rights reserved.</p>
            <p className="text-gray-500 text-[10px] sm:text-xs">
              Not affiliated with any social media platform. We never ask for your password.
            </p>
          </div>
        </div>
      </footer>

      <CartModal isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />

      <Toast
        message={toastMessage}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        type={toastType}
      />

      {showPrivateAccountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 sm:p-8 shadow-2xl relative">
            <button
              onClick={() => setShowPrivateAccountModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-purple-600" />
              </div>

              <h3 className="text-2xl font-extrabold text-gray-900 mb-3">
                Private Account
              </h3>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Please set your profile to public before proceeding. Once it is public, search again.
              </p>

              <button
                onClick={() => setShowPrivateAccountModal(false)}
                className="w-full bg-gradient-to-r from-pink-600 to-purple-600 text-white py-3 rounded-xl font-bold hover:from-pink-700 hover:to-purple-700 transition-all shadow-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showPostSelector && selectedPackage && searchResult && searchResult.username && (
        <PostSelector
          isOpen={showPostSelector}
          onClose={() => {
            setShowPostSelector(false);
            setPendingCartAction(null);
          }}
          username={searchResult.username}
          channelUrl={searchResult.channelUrl}
          quantity={selectedPackage.quantity}
          serviceType={mapServiceToBackend(selectedService)}
          platform={selectedPlatform}
          onConfirm={handlePostsSelected}
        />
      )}
    </div>
  );
}
